<template>
  <div class="modal-overlay" @keydown.esc.prevent>
    <div class="modal" @click.stop>
      <div class="modal-header">
        <h3>{{ mode === "add" ? "产品新增" : "产品编辑" }}</h3>
        <button class="close-btn" @click="handleRequestClose">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <table class="form-table">
          <tbody>
            <!-- 固定字段渲染 -->
            <tr v-for="(field, index) in fixedFields" :key="'fixed-' + index">
              <td>{{ field.label }}</td>
              <td>
                <!-- ... (select, input, ImageUpload 保持不变) ... -->
                <select
                  v-if="field.type === 'select'"
                  v-model="formData[field.key]"
                  :disabled="mode === 'edit' && field.key === 'category'"
                >
                  <option value="INTERMEDIATES">INTERMEDIATES</option>
                  <option value="FINE_CHEMICAL">FINE CHEMICAL</option>
                  <option value="OTHERS">OTHERS</option>
                </select>
                <input
                  v-else-if="field.type === 'text'"
                  v-model="formData[field.key]"
                  type="text"
                  :placeholder="field.placeholder"
                />
                <div v-else-if="field.type === 'file'" class="file-input">
                  <ImageUpload
                    v-model="formData.structuralFormula"
                    v-model:fileList="structuralFormulaFileList"
                    :max-size="2"
                    @upload-success="handleStructuralFormulaUploadSuccess"
                    @upload-error="handleStructuralFormulaUploadError"
                    @file-dialog-opened="onFileDialogOpened"
                    @file-dialog-closed="onFileDialogClosed"
                    @click.native.stop />
                </div>
              </td>
            </tr>
          </tbody>

          <!-- 动态属性拖拽区域 (保持不变) -->
          <draggable
            v-model="dynamicAttributes"
            tag="tbody"
            item-key="id"
            handle=".drag-handle"
            :animation="200"
          >
            <template #item="{ element, index }">
              <tr :key="element.id" class="dynamic-attribute-row">
                <td class="attribute-name-cell">
                  <div class="attribute-header">
                    <span class="drag-handle">☰</span>
                    <input
                      v-model="element.name"
                      type="text"
                      placeholder="属性名"
                      class="attribute-name-input"
                    />
                  </div>
                </td>
                <td class="dynamic-value-cell">
                  <div class="attribute-value-container">
                     <input
                       v-if="element.type === 'STR'"
                       v-model="element.value"
                       type="text"
                       placeholder="属性值"
                     />
                     <div v-else-if="element.type === 'IMG'" class="file-input">
                         <ImageUpload
                           v-model="element.value"
                           v-model:fileList="element.fileList"
                           :max-size="2"
                           @upload-success="(url) => handleImageAttributeUploadSuccess(url, index)"
                           @upload-error="(error) => handleImageAttributeUploadError(error, index)"
                           @file-dialog-opened="onFileDialogOpened"
                           @file-dialog-closed="onFileDialogClosed"
                           @click.native.stop />
                     </div>
                     <textarea
                       v-else-if="element.type === 'HML'"
                       v-model="element.value"
                       placeholder="请输入HTML内容"
                       rows="4"
                     ></textarea>
                     <button
                       class="attribute-btn remove-btn"
                       @click="removeDynamicAttribute(index)"
                     >
                       <i class="fas fa-times"></i>
                     </button>
                   </div>
                </td>
              </tr>
            </template>
          </draggable>

          <!-- 添加属性按钮行 -->
          <tbody>
            <tr>
              <td></td>
              <td>
                 <button
                  class="attribute-btn add-btn"
                  @click="showAddAttributeWizard = true" 
                >
                  <i class="fas fa-plus"></i> 添加属性
                </button>
               </td>
            </tr>
          </tbody>
        </table>

        <!-- 移除原来的两个小模态框的 HTML -->
        <!--
        <div v-if="showAttributeSelector" ...>...</div>
        <div v-if="showAttributeNameInput" ...>...</div>
        -->

        <!-- 引入新的子组件 -->
        <AddDynamicAttributeWizard
          v-if="showAddAttributeWizard"
          @attribute-added="handleAttributeAdded"
          @wizard-cancelled="showAddAttributeWizard = false"
        />

      </div>
      <div class="modal-footer">
        <button class="btn btn-cancel" @click="handleRequestClose">取消</button>
        <button class="btn btn-confirm" @click="saveProduct">确定</button>
      </div>
    </div>
  </div>
</template>

<script>
// ProductFormModal.vue <script> 部分

import { defineComponent, reactive, ref, watch } from "vue";
import draggable from "vuedraggable";
import { getProductAllPropertiesById } from "@/api";
import ImageUpload from "./ImageUpload.vue";
import { ElMessage } from 'element-plus';
import AddDynamicAttributeWizard from './AddDynamicAttributeWizard.vue';

export default defineComponent({
  name: "ProductFormModal",
  components: {
    draggable,
    ImageUpload,
    AddDynamicAttributeWizard,
  },
  props: {
    mode: { type: String, default: "add" },
    product: { type: Object, default: () => null },
    category: { type: String, default: "" },
  },
  emits: ['save', 'request-close'],
  setup(props, { emit }) {
    // ... (fixedFields, formData, dynamicAttributes 等 ref 和 reactive 定义保持不变) ...
    const fixedFields = ref([
        { label: "产品类型", key: "category", type: "select" },
        { label: "产品名称", key: "name", type: "text", placeholder: "请输入产品名称" },
        { label: "产品别名", key: "synonyms", type: "text", placeholder: "请输入产品别名" },
        { label: "产品CAS号", key: "cas", type: "text", placeholder: "请输入产品CAS号" },
        { label: "结构式", key: "structuralFormula", type: "file" },
    ]);
    const formData = reactive({
        id: null,
        category: props.category || "INTERMEDIATES",
        name: "",
        synonyms: "",
        cas: "",
        structuralFormula: null,
    });
    const dynamicAttributes = ref([]);
    const structuralFormulaFileList = ref([]);
    const showAddAttributeWizard = ref(false);
    const isFileDialogActive = ref(false);

    // ... (initializeForm, fetchProductProperties, onFileDialogOpened, onFileDialogClosed, 上传处理函数, handleAttributeAdded, removeDynamicAttribute, handleRequestClose 等方法保持不变) ...
    const initializeForm = async () => { /* ... */
        if (props.mode === 'edit' && props.product) {
            Object.assign(formData, {
            id: props.product.productId || null,
            category: props.product.productType || props.category,
            name: props.product.productName || "",
            synonyms: props.product.productSynonyms || "",
            cas: props.product.productCas || "",
            structuralFormula: props.product.productStrucPic || null,
            });
            structuralFormulaFileList.value = props.product.productStrucPic
            ? [{ name: props.product.productStrucPic, url: `http://image.langtaichem.com/${props.product.productStrucPic}` }]
            : [];
            await fetchProductProperties(props.product.productId);
        } else {
            Object.assign(formData, {
            id: null,
            category: props.category || "INTERMEDIATES",
            name: "",
            synonyms: "",
            cas: "",
            structuralFormula: null,
            });
            structuralFormulaFileList.value = [];
            dynamicAttributes.value = [];
        }
    };
    const fetchProductProperties = async (productId) => { /* ... */
        if (!productId) return;
        try {
            const response = await getProductAllPropertiesById(productId);
            if (response?.data) {
            dynamicAttributes.value = response.data.map((property) => {
                const isImage = property.productPropertyType === "IMG";
                const imagePath = isImage && property.productPropertyValue
                ? `http://image.langtaichem.com/${property.productPropertyValue}`
                : null;
                return {
                id: property.productPropertyId || Math.random().toString(36).slice(2),
                name: property.productPropertyName || "未命名",
                type: property.productPropertyType || "STR",
                value: property.productPropertyValue || "",
                fileList: isImage && imagePath
                    ? [{ name: property.productPropertyValue, url: imagePath }]
                    : [],
                };
            });
            dynamicAttributes.value.sort((a, b) => (a.productPropertySeq || 0) - (b.productPropertySeq || 0));
            } else {
            dynamicAttributes.value = [];
            }
        } catch (error) {
            console.error("获取产品属性失败:", error);
            ElMessage.error(`获取产品属性失败: ${error.message || '请稍后重试'}`);
            dynamicAttributes.value = [];
        }
    };
    const onFileDialogOpened = () => { isFileDialogActive.value = true; };
    const onFileDialogClosed = () => { setTimeout(() => { isFileDialogActive.value = false; }, 150); };
    const handleStructuralFormulaUploadSuccess = (uploadedKey) => {
        formData.structuralFormula = uploadedKey;
        structuralFormulaFileList.value = [{ name: uploadedKey, url: `http://image.langtaichem.com/${uploadedKey}` }];
    };
    const handleStructuralFormulaUploadError = (error) => {
        console.error("结构式上传失败:", error);
        formData.structuralFormula = null;
        structuralFormulaFileList.value = [];
    };
    const handleImageAttributeUploadSuccess = (uploadedKey, index) => {
        if (dynamicAttributes.value[index]) {
            dynamicAttributes.value[index].value = uploadedKey;
            dynamicAttributes.value[index].fileList = [{ name: uploadedKey, url: `http://image.langtaichem.com/${uploadedKey}` }];
        }
    };
    const handleImageAttributeUploadError = (error, index) => {
        console.error(`动态属性 ${index} 图片上传失败:`, error);
        if (dynamicAttributes.value[index]) {
            dynamicAttributes.value[index].value = null;
            dynamicAttributes.value[index].fileList = [];
        }
    };
     const handleAttributeAdded = ({ type, name }) => {
      const newAttr = {
        id: Math.random().toString(36).slice(2), // 前端临时 ID
        name: name,
        type: type,
        value: type === "IMG" ? null : "",
        fileList: [],
      };
      dynamicAttributes.value.push(newAttr);
      showAddAttributeWizard.value = false; // 添加成功后关闭向导
    };
    const removeDynamicAttribute = (index) => {
      dynamicAttributes.value.splice(index, 1);
    };
    const handleRequestClose = () => {
      if (isFileDialogActive.value) {
        console.warn('ProductFormModal: Close request ignored possibly due to file dialog interaction.');
        return;
      }
      emit('request-close');
    };


    // --- saveProduct 函数：改回第一次重构时的逻辑 ---
    const saveProduct = () => {
      // 基本验证
      if (!formData.name?.trim()) {
        ElMessage.warning("产品名称不能为空！");
        return;
      }
      if (!formData.category) {
        ElMessage.warning("产品类型不能为空！");
        return;
      }
      // 可选：添加更多验证...

      // 准备提交的数据
      const saveData = {
        // 固定字段 (使用 API 需要的键名)
        id: formData.id, // 编辑时需要 ID
        productType: formData.category, // 使用 productType
        productName: formData.name,     // 使用 productName
        productSynonyms: formData.synonyms,
        productCas: formData.cas,
        productStrucPic: formData.structuralFormula,
        // 动态属性 (直接转换为后端需要的格式 productAddX2List)
        productAddX2List: dynamicAttributes.value.map((attr, index) => ({
          productPropertySeq: index, // 使用当前顺序作为序号
          productPropertyType: attr.type,
          productPropertyName: attr.name,
          productPropertyValue: attr.value,
          // 如果后端需要 propertyId，并且 attr 上有这个属性，则添加
          // ...(attr.propertyId && { productPropertyId: attr.propertyId })
        })),
      };

      // 如果是新增，移除 id 字段 (根据后端 API 要求)
      if (props.mode === 'add') {
        delete saveData.id;
      }

      // console.log('Saving product (API-ready structure):', saveData);
      // emit 出去的数据结构已经与 API 期望的格式非常接近
      emit('save', saveData);
    };
    // --- saveProduct 函数结束 ---


    // --- Watchers (保持不变) ---
    watch(() => props.product, initializeForm, { immediate: true, deep: true });

    // --- 返回需要在模板中使用的数据和方法 ---
    return {
      // ... (其他返回项保持不变) ...
      fixedFields,
      formData,
      dynamicAttributes,
      structuralFormulaFileList,
      showAddAttributeWizard,
      handleRequestClose,
      saveProduct, // 返回修改后的 saveProduct
      removeDynamicAttribute,
      handleAttributeAdded,
      handleStructuralFormulaUploadSuccess,
      handleStructuralFormulaUploadError,
      handleImageAttributeUploadSuccess,
      handleImageAttributeUploadError,
      onFileDialogOpened,
      onFileDialogClosed,
      mode: props.mode,
    };
  },
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
}

.modal {
  background: #ffffff;
  width: 650px;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1001;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  background: linear-gradient(90deg, #f5f7fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
 }

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #888;
  transition: color 0.3s ease, transform 0.3s ease;
 }

.close-btn:hover {
  color: #e57373;
  transform: rotate(90deg);
}

.modal-body {
  padding: 25px;
  background: #f9f9f9;
  max-height: 500px;
  overflow-y: auto;
 }

.form-table {
  width: 100%;
  border-collapse: collapse;
}

.form-table tr {
  border-bottom: 1px solid #e9ecef;
  transition: background 0.3s ease;
 }

.form-table tr:hover {
  background: #f1f5f9;
}

.form-table td {
  padding: 12px 15px;
  vertical-align: middle;
 }

.form-table td:first-child {
  width: 160px;
  font-weight: 500;
  color: #444;
  font-size: 15px;
}

.form-table td:nth-child(2) {
  width: calc(100% - 160px);
  position: relative;
}

.form-table input,
.form-table select,
.form-table textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  box-sizing: border-box;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #fff;
}

.form-table input:focus,
.form-table select:focus,
.form-table textarea:focus {
  border-color: #5a9bd4;
  outline: none;
 }

.form-table select {
  appearance: none;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"><path fill="%23666" d="M7 10l5 5 5-5z"/></svg>')
    no-repeat right 10px center;
  background-size: 12px;
}

.dynamic-attribute-row {
  width: 100%;
  display: table-row;
}
.attribute-name-cell {
  width: 160px;
}
.dynamic-value-cell {
  width: calc(100% - 160px);
}

.attribute-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.attribute-name-input {
  flex: 1;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
}

.attribute-value-container {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.attribute-value-container input,
.attribute-value-container textarea {
  flex: 1;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
}
.attribute-value-container textarea {
  resize: vertical;
}

.file-input {
  width: 100%;
  padding: 5px 0;
}

.file-input :deep(.el-upload-list--picture) {
  margin-top: 5px;
}
.file-input :deep(.el-upload-list__item) {
  width: 100px;
  height: 100px;
  line-height: 100px;
}
.file-input :deep(.el-upload-list__item img) {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.file-input :deep(.el-button) {
  padding: 8px 12px;
  font-size: 14px;
}


.attribute-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 5px 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.add-btn {
  background: #6bb788;
  color: white;
  padding: 8px 16px;
 }

.add-btn:hover {
  background: #5aa876;
}

.remove-btn {
  border: 1px solid #e57373;
  color: #e57373;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.remove-btn:hover {
  background: #e57373;
  color: white;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 20px 25px;
  background: #f5f7fa;
  border-top: 1px solid #dee2e6;
  gap: 12px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-cancel {
  background: #ffffff;
  color: #666;
  border: 1px solid #d9d9d9;
}
.btn-cancel:hover {
  background: #f1f3f5;
}

.btn-confirm {
  background: #5a9bd4;
  color: white;
 }
.btn-confirm:hover {
  background: #4a8bc2;
}

.drag-handle {
  cursor: move;
  color: #666;
  font-size: 16px;
  padding: 5px;
 }
.form-table tr:hover .drag-handle {
  color: #5a9bd4;
}
</style>